import { ImageEditAltTextFields } from '@/components/image-library/image-edit-alt-text-fields';
import { getMaImageUrl } from '@/components/image-library/image-library-utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { updateMaImageAltText, type MaImage } from '@/lib/zybooks-api';
import { useState } from 'react';
import { toast } from 'sonner';

interface ImageEditAltTextModalProps {
    open: boolean;
    image: MaImage | null;
    onClose: () => void;
    onSaveSuccess: (updatedImage: MaImage) => void;
}

export function ImageEditAltTextModal({ open, image, onClose, onSaveSuccess }: ImageEditAltTextModalProps) {
    const [alt, setAlt] = useState(image?.alt_text ?? '');
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        if (!image) {
            return;
        }

        const trimmedAlt = alt.trim();
        if (!trimmedAlt) {
            toast.error('Alt text is required');
            return;
        }

        setIsSaving(true);
        try {
            const updatedImage = await updateMaImageAltText(image.ma_image_id, trimmedAlt);
            onSaveSuccess(updatedImage);
            onClose();
        } catch (error) {
            toast.error('Failed to update alt text', {
                description: (error as Error).message ?? 'Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={nextOpen => {
                if (!nextOpen) {
                    onClose();
                }
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit alt text</DialogTitle>
                </DialogHeader>

                {image ? (
                    <ImageEditAltTextFields
                        maImageId={image.ma_image_id}
                        alt={alt}
                        onAltChange={value => setAlt(value ?? '')}
                        previewSrc={getMaImageUrl(image)}
                        previewAlt={image.alt_text}
                        enabled={open}
                    />
                ) : null}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleSave()}
                        isLoading={isSaving}
                        disabled={!alt.trim()}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
